import React, { useState, useEffect, useCallback } from "react";
import { useB2BTranslation } from "../../B2BShared/hooks/useB2BTranslation";
import { useB2BDataLoadingState } from "../../B2BShared/hooks/useB2BLoadingState";
import { useB2BFormLoadingState } from "../../B2BShared/hooks/useB2BLoadingState";
import { useNavigate } from "react-router-dom";
import { CheckCircle, XCircle, LogOut, User, Mail } from "lucide-react";
import { useB2BAuth } from "../../B2BShared/B2BAuthContext/B2BAuthContext";
import { API_CONFIG } from "../../../../utils/apiConfig";
import "../../B2BShared/B2BButtonStyles/B2BButtonStyles.css";
import { translatePosition } from "../../B2BShared/utils/translatePosition";
import "./B2BAdminDashboard.css";

interface UserProfile {
  _id: string;
  fullName?: string;
  email: string;
  position?: string;
  businessName?: string;
  profileComplete?: boolean;
  approvedAt?: string;
  isAdmin?: boolean;
  token?: string;
}

interface ApiResponse {
  data?: UserProfile[];
  pagination?: {
    total: number;
  };
}

const B2BAdminDashboard: React.FC = () => {
  const { t, tAuth, tAdmin, tDashboard, tDashboardExists } = useB2BTranslation();
  const navigate = useNavigate();
  const { user, logout, isLoading, isAuthenticated } = useB2BAuth();
  const { isLoading: isDataLoading, error: dataError, fetchData } = useB2BDataLoadingState();
  const { isLoading: isActionLoading, handleSubmit: handleActionSubmit } = useB2BFormLoadingState();
  
  const [pendingUsers, setPendingUsers] = useState<UserProfile[]>([]);
  const [approvedUsers, setApprovedUsers] = useState<UserProfile[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [filterStatus, setFilterStatus] = useState<"pending" | "approved">("pending");

  // Check if user profile is complete using database field
  const isProfileComplete = (user: UserProfile | null | undefined): boolean => {
    if (!user) return false;

    // Use the profileComplete field from the database
    return user.profileComplete === true;
  };

  const fetchUsers = useCallback(async () => {
    await fetchData(async () => {
      // Get token from auth context first, then fallback to localStorage
      const token = user?.token || localStorage.getItem("b2bToken");

      if (!token) {
        navigate("/gcb");
        return;
      }

      if (filterStatus === "pending") {
        const response = await fetch(
          `${API_CONFIG.B2B_API_URL}/api/b2b/admin/pending-approvals`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.ok) {
          const data: ApiResponse = await response.json();
          setPendingUsers(data.data || []);
        } else {
          throw new Error(`Failed to fetch pending users: ${response.statusText}`);
        }
      } else if (filterStatus === "approved") {
        const url = `${API_CONFIG.B2B_API_URL}/api/b2b/admin/approved-users?search=${searchTerm}&limit=1000&page=1`;

        const response = await fetch(url, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (response.ok) {
          const data: ApiResponse = await response.json();
          setApprovedUsers(data.data || []);
        } else {
          throw new Error(`Failed to fetch approved users: ${response.statusText}`);
        }
      }
    });
  }, [filterStatus, user, searchTerm, navigate, fetchData]);

  useEffect(() => {
    // Check if user exists and is admin
    if (user && user.isAdmin) {
      fetchUsers();
    } else if (user && !user.isAdmin) {
      navigate("/gcb/dashboard");
      return;
    } else if (!user) {
      navigate("/gcb/login");
      return;
    }
  }, [filterStatus, user, fetchUsers, navigate]);


  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setSearchTerm(e.target.value);
  };

  const handleResendApprovalEmail = useCallback(async (userId: string) => {
    await handleActionSubmit(async () => {
      const token = user?.token || localStorage.getItem("b2bToken");
      if (!token) {
        throw new Error("Authentication required");
      }

      const response = await fetch(
        `${API_CONFIG.B2B_API_URL}/api/b2b/admin/resend-approval-email`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ userId }),
        }
      );

      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || "Failed to resend approval email");
      }

      alert(tAdmin("approvalEmailResent") || "Approval email sent successfully! Check your email inbox.");
    });
  }, [user, handleActionSubmit, tAdmin]);

  // Render loading state
  if (isLoading) {
    return (
      <div className="b2b-admin-dashboard">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>{tAdmin("loadingUsersMessage")}</p>
        </div>
      </div>
    );
  }

  // Render access denied for non-authenticated users
  if (!isAuthenticated) {
    return (
      <div className="b2b-admin-dashboard">
        <div className="access-denied">
          <h2>{tAdmin("accessDeniedTitle")}</h2>
          <p>{tAdmin("accessRequiredText")}</p>
          <button
            onClick={() => navigate("/gcb")}
            className="b2b-button b2b-button-primary"
          >
            {tAdmin("backToDashboardButton")}
          </button>
        </div>
      </div>
    );
  }

  // Render access denied for non-admin users
  if (!user?.isAdmin) {
    return (
      <div className="b2b-admin-dashboard">
        <div className="access-denied">
          <h2>{tAdmin("accessDeniedTitle")}</h2>
          <p>{tAdmin("accessRequiredText")}</p>
          <button
            onClick={() => navigate("/gcb/dashboard")}
            className="b2b-button b2b-button-primary"
          >
            {tAdmin("backToDashboardButton")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard-container">
      <div className="admin-dashboard-header">
        <div className="header-content">
          <div className="header-text">
            <h1>{tAdmin("dashboardTitle")}</h1>
            <p>{tAdmin("dashboardSubtitle")}</p>
          </div>
          <div className="b2b-button-group">
            <button
              onClick={() => navigate("/gcb/dashboard")}
              className="b2b-button b2b-button--success"
              title="Go to Member Dashboard"
            >
              <User size={16} />
              {t("dashboard.navigation.memberDashboard")}
            </button>
            <button
              onClick={logout}
              className="b2b-button b2b-button--danger"
              aria-label="Logout"
            >
              <LogOut size={16} />
              {tAuth("logout")}
            </button>
          </div>
        </div>
      </div>

      <div className="admin-controls">
        <div className="filter-tabs">
          <button
            className={`filter-tab ${filterStatus === "pending" ? "active" : ""}`}
            onClick={() => setFilterStatus("pending")}
          >
            {tAdmin("pendingApprovalsTab")}
          </button>
          <button
            className={`filter-tab ${filterStatus === "approved" ? "active" : ""}`}
            onClick={() => setFilterStatus("approved")}
          >
            {tAdmin("approvedUsersTab")}
          </button>
        </div>

        <div className="search-controls">
          <div className="search-input">
            <input
              type="text"
              placeholder={tAdmin("searchUsersPlaceholder")}
              value={searchTerm}
              onChange={handleSearchChange}
            />
          </div>
        </div>
      </div>

      <div className="admin-dashboard-content">
        {isDataLoading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>{tAdmin("loadingUsersMessage")}</p>
          </div>
        ) : dataError ? (
          <div className="error-container">
            <p>{dataError}</p>
          </div>
        ) : filterStatus === "pending" ? (
          <div className="pending-users-section">
            {pendingUsers.length === 0 ? (
              <div className="no-users-found">
                <h3>{tAdmin("noUsersFoundTitle")}</h3>
                <p>{tAdmin("noUsersFoundDescription")}</p>
              </div>
            ) : (
              <div className="users-list">
                {pendingUsers.map((user) => (
                  <div
                    key={user._id}
                    className="user-card"
                  >
                    <div className="profile-status-corner">
                      {isProfileComplete(user) ? (
                        <span className="profile-complete">
                          <CheckCircle size={16} />
                          {tAuth("profileComplete")}
                        </span>
                      ) : (
                        <span className="profile-incomplete">
                          <XCircle size={16} />
                          {tAuth("profileIncomplete")}
                        </span>
                      )}
                    </div>
                    <div className="user-info">
                      <div className="user-avatar">
                        <div className="avatar-placeholder">
                          {user.fullName
                            ? user.fullName.charAt(0).toUpperCase()
                            : "U"}
                        </div>
                      </div>
                      <div className="user-details">
                        <h3>{user.fullName || tAdmin("noNameProvidedText")}</h3>
                        <p className="user-email">{user.email}</p>
                        <p className="user-position">
                          {translatePosition(user.position, tDashboard, "profile.positionOptions", tDashboardExists) || tAdmin("notProvidedText")}
                        </p>
                        <p className="user-business">
                          {user.businessName || tAdmin("notProvidedText")}
                        </p>
                      </div>
                    </div>
                    <div className="user-actions">
                      <button
                        onClick={() =>
                          navigate(`/b2b/admin/profile/${user._id}`)
                        }
                        className="action-btn view-btn"
                      >
                        {tAdmin("viewFullProfileButton")}
                      </button>
                      <button
                        onClick={() => handleResendApprovalEmail(user._id)}
                        className="action-btn resend-email-btn"
                        disabled={isActionLoading}
                        title={tAdmin("resendApprovalEmailTooltip") || "Resend approval email with new links"}
                      >
                        <Mail size={16} />
                        {tAdmin("resendApprovalEmail") || "Resend Email"}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="approved-users-section">
            {approvedUsers.length === 0 ? (
              <div className="no-users-found">
                <h3>{tAdmin("noUsersFoundTitle")}</h3>
                <p>{tAdmin("noUsersFoundDescription")}</p>
              </div>
            ) : (
              <div className="users-list">
                {approvedUsers.map((user) => (
                  <div
                    key={user._id}
                    className="user-card"
                  >
                    <div className="profile-status-corner">
                      {isProfileComplete(user) ? (
                        <span className="profile-complete">
                          <CheckCircle size={16} />
                          {tAuth("profileComplete")}
                        </span>
                      ) : (
                        <span className="profile-incomplete">
                          <XCircle size={16} />
                          {tAuth("profileIncomplete")}
                        </span>
                      )}
                    </div>
                    <div className="user-info">
                      <div className="user-avatar">
                        <div className="avatar-placeholder">
                          {user.fullName
                            ? user.fullName.charAt(0).toUpperCase()
                            : "U"}
                        </div>
                      </div>
                      <div className="user-details">
                        <h3>{user.fullName || tAdmin("noNameProvidedText")}</h3>
                        <p className="user-email">{user.email}</p>
                        <p className="user-position">
                          {translatePosition(user.position, tDashboard, "profile.positionOptions", tDashboardExists) || tAdmin("notProvidedText")}
                        </p>
                        <p className="user-business">
                          {user.businessName || tAdmin("notProvidedText")}
                        </p>
                        <p className="user-date">
                          {t("approved_on", {
                            date: user.approvedAt
                              ? new Date(user.approvedAt).toLocaleDateString()
                              : "",
                          })}
                        </p>
                      </div>
                    </div>
                    <div className="user-actions">
                      <button
                        onClick={() =>
                          navigate(`/b2b/admin/profile/${user._id}`)
                        }
                        className="action-btn view-btn"
                      >
                        {tAdmin("viewFullProfileButton")}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

    </div>
  );
};

export default B2BAdminDashboard;


